import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ShoppingBag, Check } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { api, resolveImage, formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const CONDITION_STYLES = {
  Nuovo: "bg-emerald-500 text-white",
  Usato: "bg-blue-500 text-white",
  Vintage: "bg-purple-500 text-white",
};

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const { addItem } = useCart();

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/products/${id}`);
      setP(data);
    })();
  }, [id]);

  if (!p) return <Layout><div className="p-6 text-slate-500">Caricamento...</div></Layout>;

  const outOfStock = Number(p.quantity || 0) <= 0;
  const hasSale = p.sale_price != null && p.sale_price < p.price;
  const images = (p.images && p.images.length > 0) ? p.images : [null];

  const handleAdd = () => {
    addItem(p, 1);
    toast.success("Aggiunto al carrello", { description: p.title });
  };

  return (
    <Layout>
      <div className="px-4 pt-4 pb-32">
        <Link to="/catalogo" className="inline-flex items-center gap-1 text-sm text-slate-500 mb-3" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Indietro
        </Link>

        {/* Gallery */}
        <div className="rounded-3xl overflow-hidden bg-white border border-border">
          <div className="aspect-square bg-slate-100 relative">
            <img
              src={resolveImage(images[activeImg]) || "https://images.unsplash.com/photo-1570380888289-13cc37a74a0a?auto=format&fit=crop&w=800"}
              alt={p.title}
              className="w-full h-full object-cover"
              data-testid="product-main-image"
            />
            <div className="absolute top-3 left-3 flex flex-col gap-1.5">
              <Badge className={`${CONDITION_STYLES[p.condition] || "bg-slate-700 text-white"} text-[10px] uppercase tracking-wider font-bold`}>
                {p.condition}
              </Badge>
              {hasSale && (
                <Badge className="bg-brand-yellow text-brand-blue text-[10px] uppercase tracking-wider font-bold">
                  Offerta
                </Badge>
              )}
            </div>
          </div>
          {images.length > 1 && (
            <div className="p-2 flex gap-2 overflow-x-auto scroll-row">
              {images.map((im, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  data-testid={`thumb-${i}`}
                  className={`w-16 h-16 rounded-xl overflow-hidden border-2 flex-shrink-0 ${activeImg === i ? "border-brand-blue" : "border-transparent"}`}
                >
                  <img src={resolveImage(im)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-5">
          {p.brand && (
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{p.brand}</div>
          )}
          <h1 className="font-heading font-black text-2xl sm:text-3xl text-slate-900 mt-1" data-testid="product-title">
            {p.title}
          </h1>
          {p.year && <div className="text-sm text-slate-500 mt-1">Anno: {p.year}</div>}

          <div className="mt-4 flex items-baseline gap-3" data-testid="product-price">
            {hasSale ? (
              <>
                <span className="font-heading font-black text-3xl text-brand-blue">{formatPrice(p.sale_price)}</span>
                <span className="text-slate-400 line-through">{formatPrice(p.price)}</span>
              </>
            ) : (
              <span className="font-heading font-black text-3xl text-brand-blue">{formatPrice(p.price)}</span>
            )}
          </div>

          <div className="mt-3">
            {outOfStock ? (
              <Badge className="bg-slate-800 text-white" data-testid="product-out-of-stock">ESAURITO</Badge>
            ) : (
              <div className="inline-flex items-center gap-1 text-sm text-emerald-700">
                <Check className="w-4 h-4" /> Disponibilità: {p.quantity} {p.quantity === 1 ? "pezzo" : "pezzi"}
              </div>
            )}
          </div>

          {p.description && (
            <div className="mt-5">
              <h3 className="font-heading font-bold text-slate-900 mb-1">Descrizione</h3>
              <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{p.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* Sticky Add-to-cart */}
      <div className="fixed bottom-14 inset-x-0 z-30 border-t border-border bg-white/95 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Totale</div>
            <div className="font-heading font-black text-lg text-brand-blue">
              {formatPrice(hasSale ? p.sale_price : p.price)}
            </div>
          </div>
          <button
            data-testid="add-to-cart-btn"
            disabled={outOfStock}
            onClick={handleAdd}
            className="flex-1 h-12 rounded-full bg-brand-yellow hover:bg-brand-yellow-dark text-brand-blue font-heading font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition"
          >
            <ShoppingBag className="w-5 h-5" strokeWidth={2.5} />
            {outOfStock ? "Esaurito" : "Aggiungi al carrello"}
          </button>
        </div>
      </div>
    </Layout>
  );
}
